import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  People,
  Folder,
  BarChart,
  PieChart,
  ShowChart,
  Download,
} from '@mui/icons-material'

// Dummy data
const dummyCases = [
  { id: '1', title: 'Cuộc thi Pháp luật Dân sự 2024', year: 2024 },
  { id: '2', title: 'Cuộc thi Pháp luật Thương mại 2024', year: 2024 },
  { id: '3', title: 'Cuộc thi Pháp luật Hình sự 2023', year: 2023 },
]

const dummyStats = {
  totalCases: 12,
  totalTeams: 48,
  totalParticipants: 192,
  totalMemorials: 96,
  totalScores: 72,
  averageScore: 82.5,
  completionRate: 85.2,
}

const dummyScoreDistribution = [
  { range: '90-100', count: 15, percentage: 20.8 },
  { range: '80-89', count: 28, percentage: 38.9 },
  { range: '70-79', count: 18, percentage: 25.0 },
  { range: '60-69', count: 8, percentage: 11.1 },
  { range: '0-59', count: 3, percentage: 4.2 },
]

const dummyTopTeams = [
  { rank: 1, name: 'Đội Luật sư Xuất sắc', case: 'Cuộc thi Pháp luật Dân sự 2024', score: 95.5, members: 4 },
  { rank: 2, name: 'Đội Tư vấn Pháp lý', case: 'Cuộc thi Pháp luật Thương mại 2024', score: 93.2, members: 4 },
  { rank: 3, name: 'Đội Chuyên gia Pháp luật', case: 'Cuộc thi Pháp luật Dân sự 2024', score: 91.8, members: 4 },
  { rank: 4, name: 'Đội Nghiên cứu Pháp lý', case: 'Cuộc thi Pháp luật Hình sự 2023', score: 90.1, members: 4 },
  { rank: 5, name: 'Đội Thực hành Luật', case: 'Cuộc thi Pháp luật Thương mại 2024', score: 89.5, members: 4 },
]

const dummyCaseStats = [
  { case: 'Cuộc thi Pháp luật Dân sự 2024', teams: 16, participants: 64, memorials: 32, avgScore: 84.2, completion: 87.5 },
  { case: 'Cuộc thi Pháp luật Thương mại 2024', teams: 20, participants: 80, memorials: 40, avgScore: 81.8, completion: 90.0 },
  { case: 'Cuộc thi Pháp luật Hình sự 2023', teams: 12, participants: 48, memorials: 24, avgScore: 79.5, completion: 75.0 },
]

const dummyTrendData = [
  { month: 'Tháng 1', cases: 2, teams: 8, scores: 12 },
  { month: 'Tháng 2', cases: 3, teams: 12, scores: 18 },
  { month: 'Tháng 3', cases: 4, teams: 16, scores: 24 },
  { month: 'Tháng 4', cases: 3, teams: 12, scores: 18 },
]

export default function ReportsPage() {
  const [selectedCase, setSelectedCase] = useState<string>('all')
  const [selectedYear, setSelectedYear] = useState<string>('all')

  const filteredCaseStats = selectedCase === 'all' 
    ? dummyCaseStats 
    : dummyCaseStats.filter(s => s.case === dummyCases.find(c => c.id === selectedCase)?.title)

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Báo cáo và Phân tích
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Cuộc thi</InputLabel>
            <Select
              value={selectedCase}
              label="Cuộc thi"
              onChange={(e) => setSelectedCase(e.target.value)}
            >
              <MenuItem value="all">Tất cả cuộc thi</MenuItem>
              {dummyCases.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Năm</InputLabel>
            <Select
              value={selectedYear}
              label="Năm"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="2024">2024</MenuItem>
              <MenuItem value="2023">2023</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tổng số cuộc thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {dummyStats.totalCases}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Folder />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tổng số đội thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {dummyStats.totalTeams}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Điểm trung bình
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {dummyStats.averageScore}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tỷ lệ hoàn thành
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {dummyStats.completionRate}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <BarChart />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Score Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Phân bố điểm số
              </Typography>
              <PieChart color="action" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dummyScoreDistribution.map((item) => (
                <Box key={item.range}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {item.range} điểm
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.count} bài ({item.percentage}%)
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.percentage} 
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Top Teams */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Top 5 đội thi xuất sắc
              </Typography>
              <TrendingUp color="action" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dummyTopTeams.map((team) => (
                <Box 
                  key={team.rank}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'grey.50',
                    '&:hover': { backgroundColor: 'grey.100' }
                  }}
                >
                  <Chip 
                    label={team.rank} 
                    color={team.rank <= 3 ? 'primary' : 'default'}
                    sx={{ fontWeight: 'bold', minWidth: 40 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {team.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {team.case}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="primary.main">
                    {team.score}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Case Statistics Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Thống kê theo cuộc thi
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ShowChart color="action" />
                <Download color="action" sx={{ cursor: 'pointer' }} />
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Cuộc thi</strong></TableCell>
                    <TableCell align="right"><strong>Số đội</strong></TableCell>
                    <TableCell align="right"><strong>Số thí sinh</strong></TableCell>
                    <TableCell align="right"><strong>Số memorial</strong></TableCell>
                    <TableCell align="right"><strong>Điểm TB</strong></TableCell>
                    <TableCell align="right"><strong>Hoàn thành</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCaseStats.map((stat, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{stat.case}</TableCell>
                      <TableCell align="right">{stat.teams}</TableCell>
                      <TableCell align="right">{stat.participants}</TableCell>
                      <TableCell align="right">{stat.memorials}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={stat.avgScore.toFixed(1)} 
                          size="small" 
                          color={stat.avgScore >= 85 ? 'success' : stat.avgScore >= 80 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={stat.completion} 
                            sx={{ width: 100, height: 6, borderRadius: 1 }}
                          />
                          <Typography variant="body2">{stat.completion}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Trend Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Xu hướng theo tháng
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 200, mt: 3 }}>
              {dummyTrendData.map((data, idx) => (
                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                    <Box 
                      sx={{ 
                        width: 40, 
                        height: (data.cases / 4) * 150, 
                        backgroundColor: 'primary.main',
                        borderRadius: '4px 4px 0 0'
                      }} 
                    />
                    <Box 
                      sx={{ 
                        width: 40, 
                        height: (data.teams / 20) * 150, 
                        backgroundColor: 'secondary.main',
                        borderRadius: '4px 4px 0 0'
                      }} 
                    />
                    <Box 
                      sx={{ 
                        width: 40, 
                        height: (data.scores / 24) * 150, 
                        backgroundColor: 'success.main',
                        borderRadius: '4px 4px 0 0'
                      }} 
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {data.month}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.main', borderRadius: 1 }} />
                <Typography variant="caption">Cuộc thi</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: 'secondary.main', borderRadius: 1 }} />
                <Typography variant="caption">Đội thi</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: 'success.main', borderRadius: 1 }} />
                <Typography variant="caption">Điểm số</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}
